/* ViewsPane.js */

import React, { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AddIcon               from "@mui/icons-material/Add";
import SaveAltIcon           from "@mui/icons-material/SaveAlt";
import IosShareIcon          from "@mui/icons-material/IosShare";
import DeleteOutlineIcon     from "@mui/icons-material/DeleteOutline";
import SettingsOutlinedIcon  from "@mui/icons-material/SettingsOutlined";
import StarOutlineIcon       from "@mui/icons-material/StarOutline";
import StarIcon              from "@mui/icons-material/Star";
import EditOutlinedIcon      from "@mui/icons-material/EditOutlined";

// ── Type icons (mirrors ProjectsChoser) ──────────────────────────────
import DescriptionOutlinedIcon    from "@mui/icons-material/DescriptionOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import ManageSearchOutlinedIcon   from "@mui/icons-material/ManageSearchOutlined";
import PeopleOutlinedIcon         from "@mui/icons-material/PeopleOutlined";
import DashboardOutlinedIcon      from "@mui/icons-material/DashboardOutlined";

// ── Meta badge icons ──────────────────────────────────────────────────
import ShieldOutlinedIcon    from "@mui/icons-material/ShieldOutlined";
import LockOutlinedIcon      from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon     from "@mui/icons-material/PersonOutline";
import StarOutlinedIcon      from "@mui/icons-material/StarOutlined";

import HyperionTooltip from "../../HyperionToolTip.js";
import { isViewLocked, isViewEditable } from "../../../permissions/Permissions.js";
import { compareViewsForDisplay, viewTypeRank, viewCategory } from "../../../helpers/ViewHelpers.js";
import "./ViewsPane.css";

// ── Colour helpers (identical to ProjectsChoser) ──────────────────────
const clamp01 = (n) => Math.max(0, Math.min(1, n));

const hexToRgba = (hex, alpha) => {
  const a = clamp01(alpha);
  if (!hex || typeof hex !== "string") return `rgba(255,255,255,${a})`;
  const h = hex.replace("#", "").trim();
  if (h.length !== 6) return `rgba(255,255,255,${a})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return `rgba(255,255,255,${a})`;
  return `rgba(${r},${g},${b},${a})`;
};

const getTypeColor = (type, theme) => {
  const t = theme;
  if (type === "submissions")              return t.submissionColour;
  if (type === "submissions_under_review") return t.submissionsUnderReviewColour;
  if (type === "reviews")                  return t.reviewColour;
  if (type === "users")                    return t.userColour;
  return                                          t.mainColour;
};

const getTypeIcon = (type) => {
  if (type === "submissions")              return DescriptionOutlinedIcon;
  if (type === "submissions_under_review") return PlaylistAddCheckOutlinedIcon;
  if (type === "reviews")                  return ManageSearchOutlinedIcon;
  if (type === "users")                    return PeopleOutlinedIcon;
  return DashboardOutlinedIcon;
};

// ── ViewRow ───────────────────────────────────────────────────────────
const ViewRow = ({ view, isActive, onRename, customTheme }) => {
  const typeColor = getTypeColor(view.type, customTheme);
  const TypeIcon  = getTypeIcon(view.type);

  const isDefault  = !!view.defaultView;
  const isSystem   = !!view._isSystem;
  const isLocked   = isViewLocked(view);
  const isEditable = isViewEditable(view);

  const vLabel = typeof view.version === "number"
    ? `${view.viewLabel} · v${view.version}`
    : view.viewLabel;

  return (
    <span
      className={["vp-row", isActive ? "active" : ""].join(" ")}
      style={{ "--typeColor": typeColor }}
    >
      {/* Left colour rail */}
      <span className="vp-rail" aria-hidden="true" />

      {/* Type icon */}
      <TypeIcon className="vp-type-icon" aria-hidden="true" />

      {/* Label */}
      <span className="vp-label" title={view.viewLabel}>{vLabel}</span>

      {/* Meta badges */}
      <span className="vp-meta">
        {isDefault && (
          <HyperionTooltip title="Default view" variant="dark">
            <span className="vp-badge vp-badge--default">
              <StarOutlinedIcon className="vp-badge-icon vp-badge-icon--default" />
            </span>
          </HyperionTooltip>
        )}
        {isSystem && (
          <HyperionTooltip title="System view — platform managed, cannot be edited" variant="dark">
            <span className="vp-badge vp-badge--system">
              <ShieldOutlinedIcon className="vp-badge-icon vp-badge-icon--system" />
            </span>
          </HyperionTooltip>
        )}
        {isLocked && (
          <HyperionTooltip title="Locked — editing disabled" variant="dark">
            <span className="vp-badge vp-badge--locked">
              <LockOutlinedIcon className="vp-badge-icon vp-badge-icon--locked" />
            </span>
          </HyperionTooltip>
        )}
        {isEditable && (
          <HyperionTooltip title="Your view" variant="dark">
            <span className="vp-badge vp-badge--person">
              <PersonOutlineIcon className="vp-badge-icon vp-badge-icon--person" />
            </span>
          </HyperionTooltip>
        )}
        {isEditable && (
          <HyperionTooltip title="Rename" variant="dark">
            <span
              className="vp-badge vp-badge--edit"
              onClick={(e) => { e.stopPropagation(); onRename?.(view); }}
            >
              <EditOutlinedIcon className="vp-badge-icon vp-badge-icon--edit" />
            </span>
          </HyperionTooltip>
        )}
      </span>
    </span>
  );
};

// ── ViewsPane ─────────────────────────────────────────────────────────
/**
 * Props:
 * - views: array with { formId, viewLabel, type?, version?, defaultView?, _locked?, _isSystem? }
 * - selected: { formId, viewLabel }
 * - onSelect: (key) => void
 * - onCreate: ({ label, type, makeDefault }) => void
 * - onDelete: () => void
 * - onSetDefault: () => void
 * - onRenameView: (key, newLabel) => void
 * - viewTypes: string[]
 * - customTheme: object (optional, passed through to colour helpers)
 */
export default function ViewsPane({
  views = [],
  selected,
  onSelect,
  onCreate,
  onDelete,
  onSetDefault,
  onRenameView,
  viewTypes = [],
  userId,
  customTheme,
}) {
  const [open, setOpen]     = useState(false);
  const [label, setLabel]   = useState("");
  const [type, setType]     = useState(viewTypes[0] || "submissions");

  const anyDefault = useMemo(() => views.some((v) => v.defaultView), [views]);
  const [makeDefault, setMakeDefault] = useState(!anyDefault);

  const [renameViewOpen,  setRenameViewOpen]  = useState(false);
  const [renameTarget,    setRenameTarget]    = useState(null);
  const [renameViewLabel, setRenameViewLabel] = useState("");

  const openDialog = () => {
    setLabel("");
    setType(viewTypes[0] || "submissions");
    setMakeDefault(!anyDefault);
    setOpen(true);
  };
  const closeDialog = () => setOpen(false);

  const create = () => {
    if (!label.trim()) return;
    onCreate?.({ label: label.trim(), type, makeDefault });
    closeDialog();
  };

  const displayViews = useMemo(() => {
    const seen = new Set();
    const deduped = [];
    for (const v of views) {
      const k = `${v.formId}::${v.viewLabel}`;
      if (seen.has(k)) continue;
      seen.add(k);
      deduped.push(v);
    }
    return deduped.sort(compareViewsForDisplay(userId));
  }, [views, userId]);

  const isSelected = (v) =>
    selected?.formId === v.formId && selected?.viewLabel === v.viewLabel;

  const startRename = (v) => {
    setRenameTarget({ formId: v.formId, viewLabel: v.viewLabel });
    setRenameViewLabel(v.viewLabel || "");
    setRenameViewOpen(true);
  };
  const confirmRename = () => {
    const newLabel = renameViewLabel.trim();
    if (newLabel && renameTarget) onRenameView?.(renameTarget, newLabel);
    setRenameViewOpen(false);
  };

  const renderList = (arr) => (
    <div className="vp-list">
      {arr.map((v, i) => {
        const prev = arr[i - 1];
        const typeChanged = !!prev && viewTypeRank(prev.type) !== viewTypeRank(v.type);
        const categoryChanged =
          !!prev && !typeChanged && viewCategory(prev, userId) !== viewCategory(v, userId);
        return (
          <React.Fragment key={`${v.formId}:${v.viewLabel}:${i}`}>
            {typeChanged && <div className="cv-separator" role="separator" />}
            {categoryChanged && <div className="cv-separator cv-separator--soft" role="separator" />}
            <div
              className="vp-list-item"
              onClick={() => onSelect?.({ formId: v.formId, viewLabel: v.viewLabel })}
            >
              <ViewRow
                view={v}
                isActive={isSelected(v)}
                onRename={startRename}
                customTheme={customTheme}
              />
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  const selectedView = views.find((v) => isSelected(v));

  return (
    <Box className="cv-pane cv-pane--left">
      <Box className="cv-pane-header">
        <Typography variant="subtitle2">Views</Typography>
        <Box className="cv-icons">
          <Tooltip title="Add view">
            <IconButton size="small" onClick={openDialog}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Set default">
            <IconButton size="small" onClick={onSetDefault}>
              {selectedView?.defaultView ? (
                <StarIcon fontSize="small" />
              ) : (
                <StarOutlineIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Save view">
            <IconButton size="small">
              <SaveAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Share view">
            <IconButton size="small">
              <IosShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete view">
            <IconButton size="small" onClick={onDelete}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Settings">
            <IconButton size="small">
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className="cv-scroll">
        {renderList(displayViews)}
      </Box>

      {/* ── Add view dialog ── */}
      <Dialog open={open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Add view</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus fullWidth margin="dense"
            label="View label" value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <Box className="cv-field-block">
            <Typography variant="caption">Type</Typography>
            <Select fullWidth size="small" value={type} onChange={(e) => setType(e.target.value)}>
              {viewTypes.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </Box>
          <FormControlLabel
            className="cv-field-block--small"
            control={
              <Checkbox checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />
            }
            label="Make default"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={!label.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* ── Rename dialog ── */}
      <Dialog
        open={renameViewOpen} onClose={() => setRenameViewOpen(false)}
        maxWidth="xs" fullWidth disableScrollLock
      >
        <DialogTitle>Rename view</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus fullWidth margin="dense"
            label="New name" value={renameViewLabel}
            onChange={(e) => setRenameViewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmRename(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameViewOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmRename} disabled={!renameViewLabel.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
